import React, {useCallback, useEffect} from 'react'
import {Row, Col, H5, Paragraph, CustomerDialogButtonRow, DangerAlert} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'

import {Label, Value} from './styles'

type CompanyConfirmationDialogProps = {
  saveWithExistingCompany: (data: Record<string, unknown>, useUserInput?: boolean) => void
  params: {
    userInputData?: {
      votingShare: string
      companyName: string
      address: {
        streetName?: string
        countryCode?: string
        streetNumber?: string
        zipCode?: string
        state?: string
        city?: string
      }
    }
    existingData?: {
      externalId: string
      companyName: string
      address: {
        streetName?: string
        countryCode?: string
        streetNumber?: string
        zipCode?: string
        state?: string
        city?: string
      }
    }
  }
  errorMessage?: string
  reset: () => void
  savePending: boolean
}

const CompanyConfirmationDialog: React.FC<CompanyConfirmationDialogProps> = ({
  params,
  saveWithExistingCompany,
  errorMessage,
  reset,
  savePending,
}) => {
  const {t} = useTranslation('charon')

  useEffect(() => {
    reset()
  }, [reset])

  const handleSaveExisting = useCallback(() => {
    saveWithExistingCompany({
      externalId: params?.existingData?.externalId,
      votingShare: params?.userInputData?.votingShare,
    })
  }, [
    params?.existingData?.externalId,
    params?.userInputData?.votingShare,
    saveWithExistingCompany,
  ])

  const handleSaveUserInput = useCallback(() => {
    saveWithExistingCompany(
      {
        votingShare: params?.userInputData?.votingShare,
        companyName: params?.userInputData?.companyName,
        address: params?.userInputData?.address,
      },
      true
    )
  }, [
    saveWithExistingCompany,
    params?.userInputData?.votingShare,
    params?.userInputData?.companyName,
    params?.userInputData?.address,
  ])

  return (
    <>
      {errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row justifyContent="center">
        <Col auto>
          <H5>{t('intermediary-shareholder-page.confirmation-dialog.headline')}</H5>
        </Col>
      </Row>
      <Row justifyContent="center" mb={16}>
        <Col auto>
          <Paragraph>
            {t('intermediary-shareholder-page.confirmation-dialog.description')}
          </Paragraph>
        </Col>
      </Row>
      <Row>
        <Col>
          <Label>{t('company-form.company-name.label')}</Label>
          <Value>{params?.existingData?.companyName}</Value>
        </Col>
      </Row>
      <Row>
        <Col>
          <Label>{t('person-form.street-name.label')}</Label>
          <Value>{params?.existingData?.address?.streetName}</Value>
        </Col>
      </Row>
      <Row>
        <Col col={3}>
          <Label>{t('person-form.street-number.label')}</Label>
          <Value>{params?.existingData?.address?.streetNumber}</Value>
        </Col>
        <Col col={3}>
          <Label>{t('person-form.city.label')}</Label>
          <Value>{params?.existingData?.address?.city}</Value>
        </Col>
        <Col col={3}>
          <Label>{t('person-form.zip-code.label')}</Label>
          <Value>{params?.existingData?.address?.zipCode}</Value>
        </Col>
        <Col col={3}>
          <Label>{t('person-form.state.label')}</Label>
          <Value>{params?.existingData?.address?.state}</Value>
        </Col>
      </Row>

      <CustomerDialogButtonRow
        canSubmit={!savePending}
        canCancel={!savePending}
        loading={savePending}
        hasCancel
        cancelText={t('intermediary-shareholder-page.confirmation-dialog.user-input.cta')}
        onSubmitClick={handleSaveExisting}
        onCancelClick={handleSaveUserInput}
        submitText={t('intermediary-shareholder-page.confirmation-dialog.existing-company.cta')}
      />
    </>
  )
}

export default CompanyConfirmationDialog
